/**
 * Tiny synthesized sounds for the map toys (bells, dings, beeps), made with
 * the Web Audio API so no audio files ship. Sounds only ever play in
 * response to a click, which is what browsers require before audio can
 * start.
 */

/** One note: pitch, start offset, and length. */
export interface Tone {
  /** Pitch in Hz. */
  frequency: number;
  /** When the note starts, in seconds after the call. */
  startSec: number;
  /** How long the note rings before fading out, in seconds. */
  durationSec: number;
}

/** Peak volume of each note; kept soft so a map of toys never startles. */
const PEAK_GAIN = 0.12;

let sharedContext: AudioContext | undefined;

function audioContext(): AudioContext | undefined {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') {
    return undefined;
  }
  sharedContext ??= new AudioContext();
  return sharedContext;
}

/**
 * Plays a sequence of bell-like notes: each attacks quickly, then decays
 * exponentially over its duration. A no-op where Web Audio is unavailable.
 *
 * @param params.tones - The notes to play.
 * @param params.wave - Oscillator shape. Omitted defaults to `sine` (soft
 *     bell); `triangle` sounds brighter, for dings and beeps.
 * @return Nothing.
 * @throws Never; audio failures are logged at debug level.
 */
export function playTones({
  tones,
  wave = 'sine',
}: {
  tones: readonly Tone[];
  wave?: OscillatorType;
}): void {
  try {
    const context = audioContext();
    if (context === undefined) {
      return;
    }
    void context.resume();
    const now = context.currentTime;
    tones.forEach(({frequency, startSec, durationSec}) => {
      const start = now + startSec;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = wave;
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + durationSec);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + durationSec + 0.05);
    });
  } catch (error) {
    console.debug('[toySounds] playTones failed', error);
  }
}
